import { Injectable } from '@angular/core';
import { AssetService, FileUploadObject } from '../../_core/state/asset/asset.service';
import { Type as AssetCategory } from '../../../../../api/src/asset/asset.entity';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { dataUrlToBlob } from '../../_core/utils/data.utils';

@Injectable({ providedIn: 'root' })
export class FileUploadService {
    public files: FileUploadObject[] = [];
    public validationErrorMessage: string | null = null;

    constructor(private readonly assetService: AssetService) {}

    async addFile(event: FileList | File[], assetCategory?: AssetCategory, collectionId?: string): Promise<FileUploadObject[]> {
        const files: FileUploadObject[] = [];
        const filePromises: Promise<void>[] = [];
        this.validationErrorMessage = null;

        for (let index = 0; index < event.length; index++) {
            const f = event[index];
            const inferredCategory = assetCategory ?? this.detectCategory(f);
            const element: FileUploadObject = {
                category: inferredCategory,
                title: f.name,
                saveTo: 'dealer-files',
                uploaded: false,
                uploading: false,
                file: f,
                collectionIds: collectionId ? [collectionId] : []
            };

            if (element.category === AssetCategory.Image) {
                if (!this.assetService.isImage(element.file.type)) {
                    this.validationErrorMessage = `The file ${element.file.name} can't be uploaded to this category.`;
                    continue;
                }
                // Async: get image dimensions
                filePromises.push(
                    new Promise<void>(resolve => {
                        const oFReader = new FileReader();
                        oFReader.readAsDataURL(element.file);
                        oFReader.onload = (event: ProgressEvent<FileReader>) => {
                            const image = new Image();
                            image.src = event.target?.result as string;
                            image.onload = () => {
                                element.dimensions = { width: image.width, height: image.height };
                                files.push({ ...element });
                                resolve();
                            };
                            image.onerror = () => {
                                this.validationErrorMessage = `Could not read image dimensions for ${element.file.name}`;
                                resolve();
                            };
                        };
                        oFReader.onerror = () => {
                            this.validationErrorMessage = `Could not read file ${element.file.name}`;
                            resolve();
                        };
                    }),
                );
                continue;
            }

            if (element.category === AssetCategory.Music) {
                if (!this.assetService.isAudio(element.file.type)) {
                    this.validationErrorMessage = `The file ${element.file.name} can't be uploaded to this category.`;
                    continue;
                }
                files.push({ ...element });
                continue;
            }

            if (element.category === AssetCategory.Video) {
                if (!this.assetService.isVideo(element.file.type)) {
                    this.validationErrorMessage = `The file ${element.file.name} can't be uploaded to this category.`;
                    continue;
                }
                // Async: get video metadata
                filePromises.push(
                    new Promise<void>(resolve => {
                        const video = document.createElement('video');
                        video.preload = 'metadata';
                        video.onloadedmetadata = () => {
                            window.URL.revokeObjectURL(video.src);
                            if (video.height < video.width) {
                                this.validationErrorMessage = 'Make sure the video is in landscape view';
                                resolve();
                                return;
                            }
                            this.validationErrorMessage = null;
                            element.duration = video.duration;
                            files.push({ ...element });
                            resolve();
                        };
                        video.onerror = () => {
                            this.validationErrorMessage = `Could not read video metadata for ${element.file.name}`;
                            resolve();
                        };
                        video.src = URL.createObjectURL(element.file);
                    }),
                );
                continue;
            }

            if (element.category === AssetCategory.Voice || element.category === AssetCategory.Audio) {
                if (!this.assetService.isAudio(element.file.type)) {
                    this.validationErrorMessage = `The file ${element.file.name} can't be uploaded to this category.`;
                    continue;
                }
                files.push({ ...element });
                continue;
            }

            if (element.category === AssetCategory.Font) {
                if (!this.assetService.isWebFont(element.file)) {
                    this.validationErrorMessage = `The file ${element.file.name} is not a valid web font. You must upload a valid web font (TTF, WOFF, WOFF2).`;
                    continue;
                }
                files.push({ ...element });
                continue;
            }

            // If no specific category is matched, throw a warning.
            // Optionally, skip or add to files as needed.
        }

        await Promise.all(filePromises);
        this.files = [...this.files, ...files];
        return files;
    }

    async uploadFileFromBase64(base64String: string, fileName: string, siteId: string, assetCategory: AssetCategory, collectionId?: string): Promise<FileUploadObject | undefined> {

		const blob = dataUrlToBlob(base64String);
		const file = new File([blob], fileName, {type: blob.type});
		const newFiles = await this.addFile([file], assetCategory, collectionId);

		if (newFiles?.length) {
			const response: any = await this.upload(newFiles[0], siteId).toPromise();
			return response.body;
		}

		return undefined;

	}

    private detectCategory(file: File): AssetCategory {
        const mime = file.type?.toLowerCase() ?? '';
        const extension = file.name?.split('.').pop()?.toLowerCase() ?? '';

        if (mime.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
            return AssetCategory.Image;
        }

        if (mime.startsWith('video/') || ['mp4', 'mov', 'webm', 'mkv'].includes(extension)) {
            return AssetCategory.Video;
        }

        if (mime.startsWith('audio/') || ['mp3', 'wav', 'aac', 'ogg', 'm4a'].includes(extension)) {
            return AssetCategory.Audio;
        }

        if (['ttf', 'otf', 'woff', 'woff2'].includes(extension)) {
            return AssetCategory.Font;
        }

        return AssetCategory.Image;
    }

    upload(file: FileUploadObject, siteId: string, projectId?: string, businessId?: string, collectionIds?: string[]) {
        file.uploading = true;
        return this.assetService
            .upload(file, siteId, projectId as string, businessId as string, collectionIds || file.collectionIds)
            .pipe(
                tap(
                    event => {
                        if (event.type === HttpEventType.UploadProgress) {
                            // TODO handle progress
                        } else if (event instanceof HttpResponse) {
                            file.id = event?.body?.['id'];
                            file.uploading = false;
                            file.uploaded = true;
                        }
                    },
                    err => {
                        console.log(err);
                        file.uploaded = false;
                        file.uploading = false;
                    },
                ),
            );
    }
}
